<template>
    <v-container grid-list-md fluid>
        <v-row no-gutters class="d-flex justify-center">
            <v-col cols="12">
                <v-row no-gutters cols="12" class="my-3 justify-center">
                    <v-col cols="2">
                        <v-btn-primary
                            block
                            @click="goToDocumentRuleAddPage()"
                            data-cy="DocumentRuleTable-btn-add"
                        >
                            Ajouter
                        </v-btn-primary>
                    </v-col>
                    <v-col cols="8" class="pl-2">
                        <v-text-field
                            v-model="search"
                            append-inner-icon="$search"
                            clearable
                            @click:clear="search = ''"
                            label="Recherche"
                            outlined
                            hide-details
                            density="compact"
                        />
                    </v-col>
                    <v-col cols="2" class="pl-2">
                        <v-combobox
                            v-if="!forClient"
                            v-model="filterCompany"
                            :items="allAvailableCompanies"
                            label="Société"
                            item-value="value"
                            dense
                            outlined
                            hide-details
                            :item-title="(item) => item.key"
                            data-cy="DocumentRuleTable-search"
                        >
                        </v-combobox>
                    </v-col>
                </v-row>
            </v-col>
            <RuleTable
                :rule="'documentRule'"
                :items="items"
                :actionFunction="getActions"
                :hideDuplication="true"
                :hideIntersection="true"
                :accountingCompanyId="accountingCompanyId"
                :patternCount="patternCount"
                :firstRequestDone="firstRequestDone"
                :pagination="pagination"
                :maxHeight="maxHeightRT"
                @to-route="goToDocumentRuleEditPage($event.id)"
                @delete="
                    patternToDelete = $event;
                    deleteDialog = true;
                "
            ></RuleTable>
        </v-row>
        <oss-dialog
            v-model="deleteDialog"
            title="Supprimer"
            :error="true"
            @close="patternToDelete = undefined"
            @valid="deletePattern"
            :validLoading="deleteLoading"
            width="800px"
        >
            <h4 class="text-center">&#xc9;tes-vous sûr(e) de vouloir supprimer cette règle ?</h4>
        </oss-dialog>
    </v-container>
</template>
<script lang="ts">
import colors from "vuetify/lib/util/colors";
import { User } from "../../store/store";

import DELETE_DOCUMENT_RULE from "../../graphql/DeleteDocumentRule.gql";
import DOCUMENT_RULES_IN_SCOPE from "../../graphql/DocumentRulesInScope.gql";

import { Apollo } from "@/decorators";
import { Component, Vue, Watch, toNative } from "vue-facing-decorator";
import RuleTable from "./RuleTable.vue";

/**
 * Display list of documents rules for a company
 */
@Component({
    components: {
        RuleTable,
    },
})
export class DocumentRuleTable extends Vue {
    /**
     * TRUE if we are in Production (client) view
     * FALSE if we are in GI view */
    get forClient(): boolean {
        return !!this.$store.state.selected_company;
    }

    patternCount = 0;

    search = "";

    pagination: any = {
        page: 1,
        rowsPerPage: 20,
        sortBy: "name",
    };

    firstRequestDone = false;

    loadingStateNext = true;

    filterCompany = null;

    get filterCompanyId() {
        return this.filterCompany?.key && this.filterCompany?.value
            ? this.filterCompany.value
            : null;
    }

    get allAvailableCompanies() {
        if (this.forClient) return [];

        const companies = this.currentUser.allAvailableCompanies.map((company) => {
            return {
                value: company.id,
                key: company.name,
            };
        });
        companies.sort((a, b) => {
            if (a.key < b.key) return -1;
            if (a.key > b.key) return 1;
            return 0;
        });
        companies.unshift({ value: "", key: "Toutes mes règles" });

        return companies;
    }

    get currentUser(): User {
        return this.$store.state.user;
    }

    get accountingCompanyId() {
        return this.forClient
            ? this.$store.state.selected_company?.id
            : this.filterCompanyId || this.currentUser?.company?.id;
    }

    getActions(item) {
        let title = "";
        if (item.rules?.thirdPartyAccountableAccount) {
            title += "Compte de tiers : " + item.rules.thirdPartyAccountableAccount.name;
        }
        if (item.rules?.accountableAccount) {
            title += title ? " - " : "";
            title += "Compte : " + item.rules.accountableAccount.name;
        }
        return title;
    }

    get maxHeightRT() {
        return "calc(100vh - 160px)";
        const innHeight = window.innerHeight;
        const vhHeight = (230 / innHeight) * 100;
        const maxHeight_vh = 100 - vhHeight;

        return maxHeight_vh + "vh";
    }

    isRootProfile = false;

    @Apollo({
        query: DOCUMENT_RULES_IN_SCOPE,
        variables() {
            return {
                companyId: this.accountingCompanyId,
                forClient: !!this.filterCompanyId || this.forClient,
                search: this.search || undefined,
            };
        },
        fetchPolicy: "network-only",
        debounce: 500,
        skip() {
            return !this.accountingCompanyId;
        },
        specificResult({ data, loading, networkStatus }: any) {
            this.loading = loading;
            if (!loading) {
                if (data?.documentRulesInScope) {
                    this.firstRequestDone = true;
                    this.items = data.documentRulesInScope.slice();
                } else {
                    this.items = [];
                    this.pagination.page = 1;
                }
            }
        },
    })
    items: Array<any> = [];

    goToDocumentRuleAddPage() {
        const routeName = this.forClient
            ? "new-document-rule-sheet-client-side"
            : "new-document-rule-sheet";
        this.$router.push({ name: routeName });
    }

    goToDocumentRuleEditPage(id: string) {
        const routeName = this.forClient
            ? "document-rule-sheet-client-side"
            : "document-rule-sheet";
        this.$router.push({ name: routeName, params: { id } });
    }

    @Watch("search")
    clearBySearch() {
        this.firstRequestDone = false;
        this.pagination.page = 1;
        this.items = [];
    }

    patternToDelete;
    deleteDialog = false;
    deleteLoading = false;

    async deletePattern() {
        this.deleteLoading = true;
        if (this.patternToDelete) {
            await this.$apollo.mutate({
                mutation: DELETE_DOCUMENT_RULE,
                variables: {
                    id: this.patternToDelete.id,
                },
            });
            this.patternToDelete = undefined;
            this.refresh();
        }
        this.deleteDialog = false;
        this.deleteLoading = false;
    }

    refresh() {
        this.items = [];
        if (this.$apollo.queries && this.$apollo.queries.items) {
            this.$apollo.queries.items.refetch();
        } else {
            debugger;
        }
    }

    get labelTextColor() {
        return colors.grey.darken2;
    }

    // @Watch("filterCompanyId")
    // filterCompanyIdWatch() {
    //    this.$apollo.queries.items.refetch();
    // }
}

export default toNative(DocumentRuleTable);
</script>
